import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Download, Upload, ShieldCheck } from "lucide-react";
import { downloadJSON } from "@/lib/exporters";
import { toast } from "sonner";

function Inner() {
  const { active, refresh, setActive } = useCompany();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("new");
  const fileRef = useRef(null);

  const onBackup = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/companies/${active.id}/backup`);
      downloadJSON(`axiora-backup-${active.name.replace(/\s+/g, "-")}`, data);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Backup failed");
    } finally {
      setBusy(false);
    }
  };

  const onPickFile = () => fileRef.current?.click();

  const onRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/companies/restore", { payload, mode });
      toast.success(`Restored: ${data.counts.ledgers} ledgers, ${data.counts.vouchers} vouchers, ${data.counts.stock_items} items`);
      await refresh();
      setActive(data.company_id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Restore failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      <PageHeader title="Backup & Restore" subtitle="Snapshot your company data and restore it later" />
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl">
        <section className="border border-gray-200 rounded bg-white p-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded bg-blue-50 text-blue-700 flex items-center justify-center">
              <Download className="w-4 h-4" />
            </div>
            <h2 className="font-semibold text-gray-900">Take a Backup</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Downloads a single <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">.json</code> file with the active company, all its ledgers, vouchers and inventory items.
          </p>
          <div className="text-xs text-gray-500 mb-4">
            Active company: <span className="font-medium text-gray-900">{active?.name || "—"}</span>
          </div>
          <Button onClick={onBackup} disabled={!active || busy} className="bg-blue-700 hover:bg-blue-800 h-8" data-testid="btn-backup">
            {busy ? "Working…" : "Download backup"}
          </Button>
        </section>

        <section className="border border-gray-200 rounded bg-white p-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Upload className="w-4 h-4" />
            </div>
            <h2 className="font-semibold text-gray-900">Restore from Backup</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Upload a previously downloaded backup file. Choose how to handle the data:
          </p>
          <div className="space-y-2 mb-4 text-sm">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" name="restore-mode" value="new" checked={mode === "new"} onChange={() => setMode("new")} className="mt-1" data-testid="radio-mode-new" />
              <div>
                <div className="font-medium">Create as new company</div>
                <div className="text-xs text-gray-500">Generates a fresh company ID. Existing companies are untouched.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" name="restore-mode" value="overwrite" checked={mode === "overwrite"} onChange={() => setMode("overwrite")} className="mt-1" data-testid="radio-mode-overwrite" />
              <div>
                <div className="font-medium text-red-700">Overwrite existing company</div>
                <div className="text-xs text-gray-500">If a company with the same ID exists, it (and all its data) will be replaced.</div>
              </div>
            </label>
          </div>
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={onRestore} className="hidden" data-testid="input-restore-file" />
          <Button onClick={onPickFile} disabled={busy} variant="outline" className="h-8" data-testid="btn-pick-restore">
            {busy ? "Working…" : "Select backup file…"}
          </Button>
        </section>

        <section className="lg:col-span-2 border border-gray-200 rounded bg-gray-50 p-4 flex items-start gap-3 text-xs text-gray-600">
          <ShieldCheck className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
          <p>
            Backups are stored locally on your machine — AXIORA does not upload them anywhere.
            Store backup files in a safe place (encrypted disk / private cloud drive). The file
            contains all transactional data for the company.
          </p>
        </section>
      </div>
    </>
  );
}

export default function Backup() {
  return <RequireCompany><Inner /></RequireCompany>;
}
